/* In-app update for Ki Para Mobile (Android shell) — and the one-time catch-up
 * of Ki Para Desktop installs older than 0.5.6 (see shellContext).
 *
 * The shell displays the website, so screens never need an app update. The
 * shell itself (native part) is updated through its download folder:
 * latest.json next to the APK (written by mobile/release.sh). When a newer
 * shell exists, a dialog offers the download; Android installs it over the
 * current app (same signing key), no uninstall needed.
 */
import Swal from './theme/swal';
import KI from './theme/palette';

const CHECK_EVERY_MS = 6 * 60 * 60 * 1000;
const SNOOZE_KEY = 'kipara_update_snooze';
const UPDATE_HOST = 'http://93.127.142.191:8085/mobile/';

function newer(a, b) {
  const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return false;
}

function shellContext() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : null;
  if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
    const flavour = window.location.hostname.startsWith('dev.') ? 'dev' : 'prod';
    return { native: true, flavour, updateUrl: `${UPDATE_HOST}${flavour}/`, plugins: cap.Plugins || {} };
  }
  const k = window.kipara;
  if (k && k.mobile && k.updateUrl) return { native: true, flavour: k.flavour, updateUrl: k.updateUrl, version: k.version, plugins: {} };
  // Ki Para Desktop (Electron). Les versions < 0.5.6 ont été construites avec
  // une adresse de mise à jour sans DNS (desktop.kipara.store) : leur mise à
  // jour automatique ne démarre jamais. Le site leur propose l'installeur ;
  // à partir de 0.5.6 l'app se met à jour seule (electron-updater) et ce
  // rattrapage se tait.
  if (k && !k.mobile && k.version && (k.flavour === 'prod' || k.flavour === 'dev') && newer(DESKTOP_SELF_UPDATE_SINCE, k.version)) {
    return { native: true, desktop: true, flavour: k.flavour, updateUrl: `${DESKTOP_UPDATE_HOST}${k.flavour}/`, version: k.version, plugins: {} };
  }
  return null;
}
const DESKTOP_UPDATE_HOST = 'http://93.127.142.191:8085/';
const DESKTOP_SELF_UPDATE_SINCE = '0.5.6';
/** latest.yml d'electron-builder → { version, url } (url absolue). */
function parseLatestYml(text, base) {
  const version = (text.match(/^version:\s*(\S+)/m) || [])[1];
  const file = (text.match(/^\s*-\s*url:\s*(.+)$/m) || text.match(/^path:\s*(.+)$/m) || [])[1];
  if (!version || !file) return null;
  return { version, url: base + encodeURIComponent(file.trim()).replace(/%2F/g, '/') };
}

async function currentVersion(ctx) {
  if (ctx.version) return ctx.version;
  try {
    const app = ctx.plugins.App;
    if (app && typeof app.getInfo === 'function') return (await app.getInfo()).version;
  } catch { /* ignore */ }
  return null;
}

function openExternal(ctx, url) {
  const browser = ctx.plugins.Browser;
  if (browser && typeof browser.open === 'function') return browser.open({ url });
  window.open(url, '_system') || (window.location.href = url);
  return null;
}

async function check(force = false) {
  const ctx = shellContext();
  if (!ctx) return;
  try {
    const version = await currentVersion(ctx);
    if (!version) return;
    const res = await fetch(`${ctx.updateUrl}${ctx.desktop ? 'latest.yml' : 'latest.json'}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const latest = ctx.desktop ? parseLatestYml(await res.text(), ctx.updateUrl) : await res.json();
    if (!latest || !latest.version || !newer(latest.version, version)) return;
    const snoozed = localStorage.getItem(SNOOZE_KEY);
    if (!force && snoozed === latest.version) return;
    const r = await Swal.fire({
      title: `Nouvelle version ${latest.version} de l'application`,
      text: ctx.desktop
        ? `Vous utilisez la version ${version}. Téléchargez la mise à jour puis ouvrez le fichier : elle s'installe par-dessus, sans désinstaller, et les prochaines mises à jour se feront toutes seules.`
        : `Vous utilisez la version ${version}. Téléchargez la mise à jour puis ouvrez le fichier : elle s'installe par-dessus, sans désinstaller.`,
      icon: 'info', showCancelButton: true, confirmButtonText: 'Télécharger', cancelButtonText: 'Plus tard',
      confirmButtonColor: KI.peacock,
    });
    if (r.isConfirmed) openExternal(ctx, latest.url);
    else localStorage.setItem(SNOOZE_KEY, latest.version);
  } catch (e) {
    // offline or update host unreachable: silent, checked again later
  }
}

let started = false;
export function startMobileUpdateCheck() {
  if (started || typeof window === 'undefined' || !shellContext()) return;
  started = true;
  setTimeout(() => check(false), 4000);
  setInterval(() => check(false), CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(false); });
}

export const checkMobileUpdateNow = () => check(true);
